import type { Monaco } from './sqlMonacoTypes';

/**
 * SQL Monarch tokenizer definition for syntax highlighting.
 * Provides token rules for keywords, strings, comments, operators, etc.
 */
export const sqlMonarchTokenizer = {
  defaultToken: '',
  tokenPostfix: '.sql',
  ignoreCase: true,

  brackets: [
    { open: '[', close: ']', token: 'delimiter.square' },
    { open: '(', close: ')', token: 'delimiter.parenthesis' }
  ],

  keywords: [
    'SELECT', 'FROM', 'WHERE', 'INSERT', 'UPDATE', 'DELETE',
    'CREATE', 'ALTER', 'DROP', 'TABLE', 'VIEW', 'PROCEDURE',
    'FUNCTION', 'TRIGGER', 'INDEX', 'CONSTRAINT', 'PRIMARY',
    'FOREIGN', 'KEY', 'JOIN', 'LEFT', 'RIGHT', 'INNER', 'OUTER',
    'FULL', 'GROUP', 'ORDER', 'BY', 'HAVING', 'AS', 'ON', 'AND',
    'OR', 'NOT', 'NULL', 'IS', 'IN', 'BETWEEN', 'LIKE', 'TOP',
    'DISTINCT', 'COUNT', 'SUM', 'AVG', 'MIN', 'MAX', 'CASE',
    'WHEN', 'THEN', 'ELSE', 'END', 'WITH', 'UNION', 'ALL',
    'INTO', 'VALUES', 'SET'
  ],

  operators: [
    '=', '>', '<', '!', '~', '?', ':', '==', '<=', '>=', '!=',
    '&&', '||', '++', '--', '+', '-', '*', '/', '&', '|', '^', '%',
    '>>', '<<', '>>>', '+=', '-=', '*=', '/=', '&=', '|=', '^=',
    '%=', '>>=', '<<=', '>>>='
  ],

  symbols: /[=><!~?:&|+\-*\/\^%]+/,
  escapes: /\\(?:[abfnrtv\\\"\']|x[0-9A-Fa-f]{1,4}|u[0-9A-Fa-f]{4}|U[0-9A-Fa-f]{8})/,

  tokenizer: {
    root: [
      // Identifiers and keywords
      [/[a-zA-Z_][\w$]*/, {
        cases: {
          '@keywords': 'keyword',
          '@default': 'identifier'
        }
      }],

      // Numbers
      [/\d+/, 'number'],

      // Strings
      [/'/, { token: 'string', bracket: '@open', next: '@string' }],
      [/"/, { token: 'string.double', bracket: '@open', next: '@stringDouble' }],

      // Whitespace
      { include: '@whitespace' },

      // Delimiters and operators.
      // Only characters declared in the `brackets` array above may be emitted
      // as `@brackets` — otherwise Monaco throws "no bracket defined as: X".
      // `<` / `>` / `{` / `}` are not SQL brackets: `<` and `>` are comparison
      // operators (handled by `@symbols` below) and `{` / `}` do not occur in
      // SQL syntax at all.
      [/[()\[\]]/, '@brackets'],
      [/@symbols/, {
        cases: {
          '@operators': 'operator',
          '@default': ''
        }
      }],

      // Delimiter
      [/[;,.]/, 'delimiter']
    ],

    string: [
      [/[^']+/, 'string'],
      [/''/, 'string'],
      [/'/, { token: 'string', bracket: '@close', next: '@pop' }]
    ],

    stringDouble: [
      [/[^"]+/, 'string.double'],
      [/""/, 'string.double'],
      [/"/, { token: 'string.double', bracket: '@close', next: '@pop' }]
    ],

    whitespace: [
      [/[ \t\r\n]+/, 'white'],
      [/--.*$/, 'comment'],
      [/\/\*/, { token: 'comment.quote', next: '@comment' }]
    ],

    comment: [
      [/[^*/]+/, 'comment'],
      [/\*\//, { token: 'comment.quote', next: '@pop' }],
      [/./, 'comment']
    ]
  }
};

/**
 * Register SQL language with Monaco if not already registered.
 * Sets up Monarch tokenizer for syntax highlighting.
 */
export function registerSqlLanguage(monaco: Monaco): void {
  if (!monaco.languages.getLanguages().some((lang: any) => lang.id === 'sql')) {
    monaco.languages.register({ id: 'sql' });
    monaco.languages.setMonarchTokensProvider('sql', sqlMonarchTokenizer);
  }
}
